/**
 * Utility functions for dietary restrictions formatting
 */

// Map English values to Norwegian labels (matching Settings DB)
const DIETARY_LABELS: Record<string, string> = {
  vegetarian: 'Vegetar',
  vegan: 'Vegan',
  gluten: 'Glutenfri',
  lactose: 'Laktosefri',
  nuts: 'Nøtteallergi',
  shellfish: 'Skalldyrallergi',
  fish: 'Fiskallergi',
  eggs: 'Eggallergi',
  other: 'Annet',
};

/**
 * Format dietary restrictions for display
 * @param dietaryRestrictions - Can be string, array, null, or undefined
 * @returns Formatted comma-separated string in Norwegian, or null if no restrictions
 */
export function formatDietaryRestrictions(
  dietaryRestrictions: string | string[] | null | undefined
): string | null {
  // Handle null, undefined, empty string, or "none"
  if (
    !dietaryRestrictions ||
    dietaryRestrictions === 'none' ||
    dietaryRestrictions === 'null' ||
    (Array.isArray(dietaryRestrictions) && dietaryRestrictions.length === 0)
  ) {
    return null;
  }

  // Convert to array if string
  let restrictions: string[];
  if (typeof dietaryRestrictions === 'string') {
    // Try to parse as JSON array first
    try {
      const parsed = JSON.parse(dietaryRestrictions);
      if (Array.isArray(parsed)) {
        restrictions = parsed;
      } else {
        restrictions = [dietaryRestrictions];
      }
    } catch {
      restrictions = [dietaryRestrictions];
    }
  } else {
    restrictions = dietaryRestrictions;
  }

  // Filter out empty strings and "none"
  const validRestrictions = restrictions.filter(
    (r) => r && r !== 'none' && r !== 'null' && r.trim() !== ''
  );

  if (validRestrictions.length === 0) {
    return null;
  }

  // Map values to Norwegian labels
  const labels = validRestrictions.map(
    (value) => DIETARY_LABELS[value] || value
  );

  return labels.join(', ');
}
